/**
 * SuggestionCard — Card singolo suggerimento AI
 * Estratta da UnifiedRightPanel per riusabilità.
 */

import type { ReactNode } from 'react';
import {
  X, Loader2, CheckSquare, Square,
  Send, ListTodo,
  CircleSlash, Clock, Edit2,
  Link2, MessageCircleQuestion, MessageSquareShare, Group, Search,
  AlertTriangle, TrendingUp, Frown, ShieldAlert,
  Timer, Gem, FileSignature, CreditCard, Zap,
} from 'lucide-react';
import { getTypeLabel, getTypeBadgeColor, getTypeIcon } from './unified-panel-helpers';
import type { AgentRequest, AgentAlertTag } from '@/lib/types/whatsappAgent';
import { tip, iconTip } from '@/components/messages/ChatTooltip';

// ============ ALERT TAG CONFIG (Notion-style) ============

const ALERT_TAG_CONFIG: Record<AgentAlertTag, { label: string; dot: string; Icon: typeof AlertTriangle }> = {
  needs_quotation:    { label: 'Da quotare',        dot: '#EB5757', Icon: AlertTriangle },
  scope_creep:        { label: 'Fuori accordo',      dot: '#F2994A', Icon: TrendingUp },
  client_frustration: { label: 'Frustrazione',       dot: '#E2B203', Icon: Frown },
  escalation_risk:    { label: 'Rischio lamentela',  dot: '#EB5757', Icon: ShieldAlert },
  delay_complaint:    { label: 'Ritardo',            dot: '#E2B203', Icon: Timer },
  upsell_opportunity: { label: 'Opportunità',        dot: '#0F7B6C', Icon: Gem },
  contract_change:    { label: 'Modifica contratto', dot: '#9065B0', Icon: FileSignature },
  payment_issue:      { label: 'Pagamento',          dot: '#D44C47', Icon: CreditCard },
  urgent:             { label: 'Urgente',            dot: '#EB5757', Icon: Zap },
};

// ============ TYPES ============

export interface SuggestionCardActions {
  onToggleSelect: (id: string) => void;
  onSendNow: (requestId: string) => Promise<void>;
  onOpenEditResponse: (requestId: string, currentText: string) => void;
  onSendEditedResponse: (requestId: string, text: string) => Promise<void>;
  onUseResponse: (text: string) => void;
  onOpenClarification: (requestId: string) => void;
  onSendClarification: (requestId: string, text: string) => Promise<void>;
  onToggleScope: (requestId: string, currentIsOutOfScope: boolean) => void;
  onDismiss: (requestId: string) => void;
  onCreateTask?: (request: AgentRequest) => void;
  onOpenLinkTask: (requestId: string) => void;
  onLinkTask: (taskId: string, taskTitle: string) => Promise<void>;
  onUnlinkTask: (requestId: string, taskId: string) => Promise<void>;
  onOpenForward: (requestId: string) => void;
  onForwardSend: (teamChatId: string) => Promise<void>;
  onUngroupRequest: (requestId: string) => void;
}

export interface SuggestionCardState {
  isSelected: boolean;
  isExpanded: boolean;
  sendingId: string | null;
  editResponseId: string | null;
  editResponseText: string;
  clarificationId: string | null;
  clarificationText: string;
  sendingClarificationId: string | null;
  forwardingId: string | null;
  forwardNote: string;
  sendingForward: boolean;
  teamChats: { id: string; name: string | null }[];
  linkingTaskId: string | null;
  projectTasks: { id: string; title: string; status: string }[];
  taskSearchQuery: string;
  hasProject: boolean;
}

export interface SuggestionCardSetters {
  setExpandedId: (id: string | null) => void;
  setEditResponseId: (id: string | null) => void;
  setEditResponseText: (text: string) => void;
  setClarificationId: (id: string | null) => void;
  setClarificationText: (text: string) => void;
  setForwardingId: (id: string | null) => void;
  setForwardNote: (text: string) => void;
  setLinkingTaskId: (id: string | null) => void;
  setTaskSearchQuery: (text: string) => void;
}

interface SuggestionCardProps {
  suggestion: AgentRequest;
  allSuggestions: AgentRequest[];
  actions: SuggestionCardActions;
  state: SuggestionCardState;
  setters: SuggestionCardSetters;
  sourcePreview: { messages: Array<{ text: string; sender: string; timestamp: number }> } | null;
  compact?: boolean;
  /** Azioni sul CRM proposte (SuggestionCrmActions), solo con Lead/Cliente collegato. */
  crmActions?: ReactNode;
}

// ============ COMPONENT ============

export function SuggestionCard({
  suggestion, allSuggestions, actions, state, setters, sourcePreview, compact = false, crmActions,
}: SuggestionCardProps) {
  // Suggerimento con sole azioni sul CRM: niente task, chiarimenti o collegamenti.
  const isCrmOnly = suggestion.request_type === 'crm_update';
  const hasResponse = suggestion.response_message && !suggestion.response_sent_at;
  const isOutOfScope = suggestion.is_out_of_scope === true;
  const alertTags: AgentAlertTag[] = Array.isArray(suggestion.alert_tags) ? suggestion.alert_tags : [];
  const hasAlertTags = alertTags.length > 0;
  const hasHighPriorityAlert = alertTags.some(t => t === 'escalation_risk' || t === 'urgent' || t === 'needs_quotation');
  const isWaitingClarification = suggestion.status === 'waiting_clarification';
  const isEditingResponse = state.editResponseId === suggestion.id;
  const isClarifying = state.clarificationId === suggestion.id;
  const isSending = state.sendingId === suggestion.id;
  const isSendingClarification = state.sendingClarificationId === suggestion.id;

  return (
    <div
      className={`rounded-lg border transition-all ${compact ? 'mt-1.5' : ''} ${
        hasHighPriorityAlert ? 'border-l-2 border-l-[#EB5757] border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-gray-800/50'
          : isOutOfScope ? 'border-l-2 border-l-[#F2994A] border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-gray-800/50 opacity-80'
            : hasAlertTags ? 'border-l-2 border-l-[#E2B203] border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-gray-800/50'
              : state.isSelected ? 'border-[#2383E2] dark:border-[#2383E2] bg-[rgba(35,131,226,0.04)] dark:bg-[#2383E2]/5'
                : 'border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-gray-800/50 hover:bg-[rgba(55,53,47,0.02)]'
      }`}
    >
      <div className={compact ? 'p-2' : 'p-3'}>
        {/* Top row */}
        <div className="flex items-start gap-2">
          <button onClick={() => actions.onToggleSelect(suggestion.id)} className="flex-shrink-0 mt-0.5"
            role="checkbox" aria-checked={state.isSelected} aria-label={`Seleziona: ${suggestion.title}`}>
            {state.isSelected
              ? <CheckSquare className="w-3.5 h-3.5 text-blue-500" />
              : <Square className="w-3.5 h-3.5 text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white" />}
          </button>
          <div className="flex-1 min-w-0">
            {/* Type + confidence + scope */}
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <span className="text-xs">{getTypeIcon(suggestion.request_type)}</span>
              <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${getTypeBadgeColor(suggestion.request_type)}`}>
                {getTypeLabel(suggestion.request_type)}
              </span>
              <span className="text-[10px] text-[#6F6E69] tabular-nums" {...tip("Quanto l'assistente è sicuro di aver capito bene")}>{suggestion.ai_confidence}%</span>
              {isOutOfScope && (
                <button
                  onClick={() => actions.onToggleScope(suggestion.id, true)}
                  {...tip('Non rientra in quanto concordato con il cliente. Clicca se invece è compreso')}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-sm bg-[rgba(242,153,74,0.08)] text-[#F2994A] hover:bg-[rgba(242,153,74,0.15)] transition-colors"
                >
                  <CircleSlash className="w-2.5 h-2.5" /> Da valutare
                </button>
              )}
              {alertTags.map(tag => {
                const cfg = ALERT_TAG_CONFIG[tag];
                if (!cfg) return null;
                const { Icon } = cfg;
                return (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-sm"
                    style={{ backgroundColor: `${cfg.dot}0D`, color: cfg.dot }}
                  >
                    <Icon className="w-2.5 h-2.5" /> {cfg.label}
                  </span>
                );
              })}
              {isWaitingClarification && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-sm bg-[rgba(226,178,3,0.08)] text-[#E2B203]">
                  <Clock className="w-2.5 h-2.5" /> In attesa di risposta
                </span>
              )}
              {suggestion.sender_name && <span className="text-[10px] text-[#6F6E69] truncate ml-auto">da {suggestion.sender_name}</span>}
            </div>

            {/* Title */}
            <p className={`text-xs text-[#37352F] dark:text-white font-medium cursor-pointer ${state.isExpanded ? '' : 'line-clamp-2'}`}
              onClick={() => setters.setExpandedId(state.isExpanded ? null : suggestion.id)}>
              {suggestion.title}
            </p>

            {/* Description */}
            {suggestion.description && (
              <p className={`text-[11px] text-[#37352F]/70 dark:text-white/60 mt-0.5 leading-relaxed ${state.isExpanded ? '' : 'line-clamp-2'}`}>
                {suggestion.description}
              </p>
            )}

            {/* Matched tasks — Notion style */}
            {suggestion.matched_tasks && suggestion.matched_tasks.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {suggestion.matched_tasks.map(task => (
                  <span key={task.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 text-[#37352F] dark:text-white/80">
                    <Link2 className="w-2.5 h-2.5 text-[#6F6E69]" />{task.title}
                    <button onClick={(e) => { e.stopPropagation(); actions.onUnlinkTask(suggestion.id, task.id); }} className="ml-0.5 text-[#6F6E69] hover:text-[#EB5757]" {...iconTip('Scollega il task')}><X className="w-2 h-2" /></button>
                  </span>
                ))}
              </div>
            )}

            {/* Group badge — Notion style */}
            {suggestion.group_id && (() => {
              const groupCount = allSuggestions.filter(s => s.group_id === suggestion.group_id).length;
              return groupCount > 1 ? (
                <span className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 text-[10px] font-medium rounded-sm bg-[rgba(144,101,176,0.06)] text-[#9065B0]">
                  <Group className="w-2.5 h-2.5" /> Unite ({groupCount})
                  <button onClick={(e) => { e.stopPropagation(); actions.onUngroupRequest(suggestion.id); }} className="ml-0.5 hover:text-[#EB5757]" {...iconTip('Separa dalle altre richieste')}><X className="w-2 h-2" /></button>
                </span>
              ) : null;
            })()}

            {/* Scope note — Notion callout style */}
            {suggestion.scope_note && (isOutOfScope || hasAlertTags) && (
              <div className="mt-1.5 pl-2.5 border-l-2 border-[rgba(242,153,74,0.4)]">
                <p className="text-[10px] text-[#37352F]/70 dark:text-white/60 leading-snug line-clamp-2">
                  {suggestion.scope_note}
                </p>
              </div>
            )}

            {/* AI Reasoning */}
            {suggestion.ai_reasoning && (
              <p className="text-[10px] text-[#6F6E69] mt-1 leading-snug line-clamp-2">
                {suggestion.ai_reasoning}
              </p>
            )}
          </div>
        </div>

        {/* Response ready chip — Notion style */}
        {hasResponse && !isEditingResponse && (
          <div className="ml-6 mt-1.5">
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-[rgba(35,131,226,0.04)] dark:bg-[#2383E2]/5">
              <Send className="w-2.5 h-2.5 text-[#2383E2] flex-shrink-0" />
              <p className="text-[10px] text-[#37352F]/80 dark:text-white/70 line-clamp-1">{suggestion.response_message}</p>
            </div>
          </div>
        )}

        {/* Source messages preview — Notion quote style */}
        {sourcePreview && !compact && sourcePreview.messages.length > 0 && (
          <div className="ml-6 mt-1.5 pl-2.5 border-l-2 border-[rgba(55,53,47,0.09)] dark:border-white/10 space-y-1">
            {sourcePreview.messages.map((msg, idx) => (
              <div key={idx} className="text-[10px] text-[#6F6E69] leading-relaxed">
                {msg.timestamp > 0 && (
                  <span className="text-[10px] text-[#6F6E69] mr-1">
                    {new Date(msg.timestamp * 1000).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })},{' '}
                    {new Date(msg.timestamp * 1000).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                {msg.sender && <span className="font-medium text-[#37352F]/70 dark:text-white/60">{msg.sender}: </span>}
                {msg.text}
              </div>
            ))}
          </div>
        )}

        {/* Expanded response — Notion callout style */}
        {hasResponse && state.isExpanded && !isEditingResponse && (
          <div className="ml-6 mt-1.5 p-2.5 rounded-sm bg-[rgba(35,131,226,0.04)] dark:bg-[#2383E2]/5">
            <p className="text-[10px] font-semibold text-[#6F6E69] mb-1 uppercase tracking-wider">Risposta suggerita</p>
            <p className="text-[11px] text-[#37352F] dark:text-white/90 leading-relaxed whitespace-pre-wrap">{suggestion.response_message}</p>
          </div>
        )}

        {/* Edit response textarea */}
        {isEditingResponse && (
          <div className="ml-6 mt-1.5">
            <textarea value={state.editResponseText} onChange={e => setters.setEditResponseText(e.target.value)} rows={3} autoFocus
              className="w-full text-[11px] text-[#37352F] dark:text-white/90 bg-white dark:bg-gray-900 border border-blue-400 rounded-lg px-2 py-1.5 outline-none resize-none" placeholder="Modifica la risposta..." />
            <div className="flex gap-1 mt-1">
              <button onClick={() => actions.onSendEditedResponse(suggestion.id, state.editResponseText)} disabled={isSending || !state.editResponseText.trim()}
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded transition-colors">
                {isSending ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Send className="w-2.5 h-2.5" />} Invia
              </button>
              <button onClick={() => setters.setEditResponseId(null)} className="px-2 py-1 text-[10px] text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white">Annulla</button>
            </div>
          </div>
        )}

        {/* Clarification textarea */}
        {isClarifying && (
          <div className="ml-6 mt-1.5">
            <textarea value={state.clarificationText} onChange={e => setters.setClarificationText(e.target.value)} rows={2} autoFocus
              className="w-full text-[11px] text-[#37352F] dark:text-white/90 bg-white dark:bg-gray-900 border border-amber-400 rounded-lg px-2 py-1.5 outline-none resize-none" placeholder="Domanda di chiarimento..." />
            <div className="flex gap-1 mt-1">
              <button onClick={() => actions.onSendClarification(suggestion.id, state.clarificationText)} disabled={isSendingClarification || !state.clarificationText.trim()}
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded transition-colors">
                {isSendingClarification ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Send className="w-2.5 h-2.5" />} Invia
              </button>
              <button onClick={() => setters.setClarificationId(null)} className="px-2 py-1 text-[10px] text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white">Annulla</button>
            </div>
          </div>
        )}

        {/* Clarification already sent */}
        {isWaitingClarification && suggestion.clarification_message && !isClarifying && (
          <div className="ml-6 mt-1.5 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/60">
            <p className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 mb-0.5 uppercase tracking-wide">Chiarimento inviato</p>
            <p className="text-[11px] text-[#37352F] dark:text-white">{suggestion.clarification_message}</p>
          </div>
        )}

        {crmActions}

        {/* Actions row — Notion style */}
        <div className="flex items-center gap-0.5 mt-2 ml-6 flex-wrap">
          {hasResponse && !isEditingResponse && !isClarifying && (
            <>
              <button onClick={() => actions.onSendNow(suggestion.id)} disabled={isSending}
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-[#2383E2] hover:bg-[#1B6EC2] disabled:opacity-50 rounded-sm transition-colors">
                {isSending ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Send className="w-2.5 h-2.5" />} Invia
              </button>
              <button onClick={() => actions.onOpenEditResponse(suggestion.id, suggestion.response_message!)}
                className="flex items-center gap-1 px-1.5 py-1 text-[10px] font-medium text-[#2383E2] hover:bg-[rgba(35,131,226,0.06)] rounded-sm transition-colors">
                <Edit2 className="w-2.5 h-2.5" /> Modifica
              </button>
              <button onClick={() => actions.onUseResponse(suggestion.response_message!)}
                {...tip('Copia la risposta nel campo di scrittura, per modificarla prima di inviarla')}
                className="px-1.5 py-1 text-[10px] text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.04)] rounded-sm transition-colors">Nel messaggio</button>
            </>
          )}
          {suggestion.status === 'new' && actions.onCreateTask && !isClarifying && !isCrmOnly && (
            <button onClick={() => actions.onCreateTask!(suggestion)} {...tip('Crea un Task commerciale da questa richiesta')} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/6 rounded-sm transition-colors">
              <ListTodo className="w-2.5 h-2.5 text-[#6F6E69]" /> Crea task
            </button>
          )}
          {(suggestion.status === 'new' || isWaitingClarification) && !isClarifying && !isEditingResponse && !isCrmOnly && (
            <button onClick={() => actions.onOpenClarification(suggestion.id)} {...tip('Scrivi al cliente una domanda per capire meglio la richiesta')} className="flex items-center gap-1 px-1.5 py-1 text-[10px] text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/6 rounded-sm transition-colors">
              <MessageCircleQuestion className="w-2.5 h-2.5" /> {isWaitingClarification ? 'Chiedi di nuovo' : 'Chiedi chiarimenti'}
            </button>
          )}
          {suggestion.status === 'new' && state.hasProject && !isClarifying && !isEditingResponse && !isCrmOnly && (
            <button onClick={() => actions.onOpenLinkTask(suggestion.id)} className="p-1 text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/6 rounded-sm transition-colors" {...iconTip('Collega a un task del progetto')}>
              <Link2 className="w-2.5 h-2.5" />
            </button>
          )}
          {!isClarifying && !isEditingResponse && (
            <button onClick={() => actions.onOpenForward(suggestion.id)} className="p-1 text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/6 rounded-sm transition-colors" {...iconTip('Inoltra ai colleghi nella chat di team')}>
              <MessageSquareShare className="w-2.5 h-2.5" />
            </button>
          )}
          <button onClick={() => actions.onDismiss(suggestion.id)} className="ml-auto p-1 rounded-sm hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/6 transition-colors" {...iconTip('Scarta il suggerimento')}>
            <X className="w-3 h-3 text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white" />
          </button>
        </div>

        {/* Forward panel */}
        {state.forwardingId === suggestion.id && (
          <div className="ml-6 mt-1.5 p-2 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200/60 dark:border-green-800/40">
            <p className="text-[10px] font-semibold text-green-700 dark:text-green-400 mb-1.5 uppercase tracking-wide">Inoltra ai colleghi</p>
            <textarea value={state.forwardNote} onChange={e => setters.setForwardNote(e.target.value)} rows={2}
              className="w-full text-[11px] bg-white dark:bg-gray-900 border border-green-300 dark:border-green-800 rounded px-2 py-1 outline-none resize-none mb-1.5" placeholder="Nota (opzionale)..." />
            <div className="flex flex-wrap gap-1">
              {state.teamChats.map(tc => (
                <button key={tc.id} onClick={() => actions.onForwardSend(tc.id)} disabled={state.sendingForward}
                  className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded transition-colors">
                  {state.sendingForward ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Send className="w-2.5 h-2.5" />} {tc.name || 'Chat'}
                </button>
              ))}
              {state.teamChats.length === 0 && <p className="text-[10px] text-[#6F6E69]">Nessuna chat di team</p>}
              <button onClick={() => setters.setForwardingId(null)} className="px-2 py-1 text-[10px] text-[#6F6E69] hover:text-[#37352F]">Annulla</button>
            </div>
          </div>
        )}

        {/* Link task panel */}
        {state.linkingTaskId === suggestion.id && (
          <div className="ml-6 mt-1.5 p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200/60 dark:border-indigo-800/40">
            <p className="text-[10px] font-semibold text-indigo-700 dark:text-indigo-400 mb-1.5 uppercase tracking-wide">Collega a un task del progetto</p>
            <div className="flex items-center gap-1 mb-1.5">
              <Search className="w-2.5 h-2.5 text-[#6F6E69]" />
              <input type="text" value={state.taskSearchQuery} onChange={e => setters.setTaskSearchQuery(e.target.value)} autoFocus
                className="flex-1 text-[11px] bg-white dark:bg-gray-900 border border-indigo-300 dark:border-indigo-800 rounded px-2 py-1 outline-none" placeholder="Cerca task..." />
              <button onClick={() => setters.setLinkingTaskId(null)} className="p-0.5 text-[#6F6E69] hover:text-[#37352F]" {...iconTip('Chiudi')}><X className="w-2.5 h-2.5" /></button>
            </div>
            <div className="max-h-28 overflow-y-auto space-y-0.5">
              {state.projectTasks.filter(t => !state.taskSearchQuery || t.title.toLowerCase().includes(state.taskSearchQuery.toLowerCase())).slice(0, 10).map(task => (
                <button key={task.id} onClick={() => actions.onLinkTask(task.id, task.title)}
                  className="w-full text-left px-2 py-1 text-[11px] text-[#37352F] dark:text-white hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded transition-colors truncate">
                  {task.title} <span className="text-[10px] text-[#6F6E69]">[{task.status}]</span>
                </button>
              ))}
              {state.projectTasks.length === 0 && <p className="text-[10px] text-[#6F6E69] py-1">Nessuna task</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
